import type {
    IFontBackground,
    IFontGlow,
    IFontShadow,
    IFontStroke,
    ILayerProps,
    ITextLayerProps,
} from '@/version-types';

export const resolveVisibleBackground = (backgrounds: IFontBackground[] | undefined): IFontBackground | null => {
    return backgrounds?.find((background) => !background.hidden) ?? null;
};

export class EffectCreator {
    private constructor() {
        throw new Error('EffectCreator is a static class');
    }

    private static readonly baseFontSize = 36;

    public static getRandomColor() {
        return `#${Math.floor(Math.random() * 0xffffff)
            .toString(16)
            .padStart(6, '0')}ff`;
    }

    public static createStroke(fontSize: number, outerColor?: string): IFontStroke {
        const ratio = fontSize / this.baseFontSize;
        const width = Math.round(2 * ratio);
        const color = outerColor || this.getRandomColor();

        return {
            width,
            color,
            direction: 0,
            hidden: false,
            editable: true,
        };
    }

    public static createGlow(fontSize: number, outerColor?: string): IFontGlow {
        const ratio = fontSize / this.baseFontSize;
        const width = Math.round(5 * ratio);
        const color = outerColor || '#fb35ddff';

        return {
            width,
            blur: 5,
            color,
            hidden: false,
            editable: true,
        };
    }

    public static createShadow(fontSize: number, outerColor?: string): IFontShadow {
        const color = outerColor || '#000000ff';
        const ratio = fontSize / this.baseFontSize;
        const y = Math.round(2.8 * ratio);

        return {
            blur: 2,
            color,
            x: 0,
            y,
            hidden: false,
            editable: true,
        };
    }

    public static createBackground(fontSize: number, outerColor?: string): IFontBackground {
        const color = outerColor || '#fcca00ff';
        const roundWeight = Math.ceil(fontSize / 2);

        return {
            color,
            roundWeight,
            hidden: false,
            editable: true,
        };
    }
}

export const FeatureChecker = {
    usingGradient: (layer: ILayerProps) => {
        const { textGradient } = layer.data as ITextLayerProps;
        return !!textGradient && textGradient.colors.length > 0;
    },

    usingTexture: (layer: ILayerProps) => {
        const { texture } = layer.data as ITextLayerProps;
        return !!texture && !!texture.url;
    },

    usingStroke: (layer: ILayerProps, options = { includeHidden: false }) => {
        const { textStroke } = layer.data as ITextLayerProps;
        const hasStroke = !!textStroke && textStroke.length > 0;

        if (options.includeHidden) {
            return hasStroke;
        }

        return hasStroke && textStroke.filter((stroke) => !stroke.hidden).length > 0;
    },

    usingShadow: (layer: ILayerProps, options = { includeHidden: false }) => {
        const { textShadow } = layer.data as ITextLayerProps;
        const hasShadow = !!textShadow && textShadow.length > 0;

        if (options.includeHidden) {
            return hasShadow;
        }

        return hasShadow && textShadow.filter((shadow) => !shadow.hidden).length > 0;
    },

    usingGlow: (layer: ILayerProps, options = { includeHidden: false }) => {
        const { textGlow } = layer.data as ITextLayerProps;
        const hasGlow = !!textGlow && textGlow.length > 0;

        if (options.includeHidden) {
            return hasGlow;
        }

        return hasGlow && textGlow.filter((glow) => !glow.hidden).length > 0;
    },

    usingHighlight: (layer: ILayerProps) => {
        const { textMark, textContents = [] } = layer.data as ITextLayerProps;

        if (textMark) return true;
        if (textContents.length > 0) {
            return textContents.some((item) => !!item.textMark);
        }

        return false;
    },

    usingShapeText: (layer: ILayerProps) => {
        const { shapeExtension } = layer.data as ITextLayerProps;
        return !!shapeExtension;
    },

    usingArtText: (layer: ILayerProps) => {
        const { advancedTextMaterial } = layer.data as ITextLayerProps;
        return !!advancedTextMaterial;
    },

    usingBackground: (layer: ILayerProps) => {
        const { textBackground } = layer.data as ITextLayerProps;
        return textBackground && textBackground.length > 0 && textBackground[0].color;
    },

    hasNoEffects: (layer: ILayerProps) => {
        const { textShadow = [], textStroke = [], textGlow = [], textBackground = [] } = layer.data as ITextLayerProps;

        const effects = [textShadow, textStroke, textGlow, textBackground];

        return !effects.some((effect) => effect.length > 0);
    },
};
